import { isDefined } from './general';
/**
 * Funcion para utilizar con el metodo Array.find() que permite buscar un item por id.
 *
 * Los elementos de la lista deben tener el atributo "id", que puede ser de cualquier tipo.
 * @param id id del elemento a buscar. Debe ser del mismo tipo que el de los elementos.
 *
 * @example return personas.find(byId(4)) -> Devuelve la persona con id 4 o undefined.
 */
export function byId(id) {
    return (item) => item.id === id;
}
/**
 * Funcion para utilizar con el metodo Array.find() que permite buscar un item por "code" (en ingles).
 *
 * Los elementos de la lista deben tener el atributo "code" (en ingles) de tipo String.
 * @param code codigo del elemento a buscar
 *
 * @example return personas.find(byCode('ASD')) -> Devuelve la persona con code 'ASD' o undefined.
 */
export function byCode(code) {
    return (item) => item.code === code;
}
/**
 * Funcion para utilizar con el metodo Array.find() que permite buscar un item por codigo.
 *
 * Los elementos de la lista deben tener el atributo codigo de tipo String.
 * @param code codigo del elemento a buscar
 *
 * @example return personas.find(byCodigo('ASD')) -> Devuelve la persona con codigo 'ASD' o undefined.
 */
export function byCodigo(codigo) {
    return (item) => item.codigo === codigo;
}
/**
 * Construye una funcion de comparacion en base a un criterio de ordenamiento
 * @param sorting
 */
export function buildCompareFn(sorting) {
    return function compare(a, b) {
        const multiplier = sorting.descending ? -1 : 1;
        const x = a[sorting.field];
        const y = b[sorting.field];
        return multiplier * (x < y ? -1 : x > y ? 1 : 0);
    };
}
/**
 * Inserta o actualiza un item de la lista, buscandolo por el predicado que se pasa por parametro y devuelve una nueva lista.
 *
 * Para que funcione bien, se debe sobreescribir la lista anterior con esta.
 *
 * @param list
 * @param item
 * @param predicate funcion que determina si el item es el buscado
 */
export function insertOrUpdate(list, item, predicate) {
    const existing = list.findIndex(predicate);
    if (existing > -1) {
        list = Object.assign([], list, { [existing]: item });
    }
    else {
        list = [...list, item];
    }
    return list;
}
/**
 * Inserta o actualiza un item de la lista, buscandolo por id y devuelve una nueva lista.
 *
 * Para que funcione bien, se debe sobreescribir la lista anterior con esta
 * @param list
 * @param item
 */
export function insertOrUpdateById(list, item) {
    return insertOrUpdate(list, item, byId(item.id));
}
/**
 * Inserta o actualiza un item de la lista, buscandolo por `code` y devuelve una nueva lista.
 *
 * Para que funcione bien, se debe sobreescribir la lista anterior con esta
 * @param list
 * @param item
 */
export function insertOrUpdateByCode(list, item) {
    return insertOrUpdate(list, item, byCode(item.code));
}
/**
 * Inserta o actualiza un item de la lista, buscandolo por `codigo` y devuelve una nueva lista.
 *
 * Para que funcione bien, se debe sobreescribir la lista anterior con esta
 * @param list
 * @param item
 */
export function insertOrUpdateByCodigo(list, item) {
    return insertOrUpdate(list, item, byCodigo(item.codigo));
}
/**
 * Funcion para usar con reduce() que devuelve la suma de los elementos
 *
 * @param acum
 * @param value
 */
export function reduceSum(acum, value) {
    return acum + value;
}
/**
 * Suma los valores de un atributo de un array de objetos
 * @param items array de objetos
 * @param field atributo que se debe sumar
 */
export function sumItems(items, field) {
    if (!items)
        return 0;
    return items.map(i => (i[field] || 0)).reduce(reduceSum, 0);
}
export function min(items, field) {
    if (!items)
        return null;
    return items.map(i => i[field]).reduce((result, value) => (result === null || value < result ? value : result), null);
}
export function max(items, field) {
    if (!items)
        return null;
    return items.map(i => i[field]).reduce((result, value) => (result === null || value > result ? value : result), null);
}
/**
 * Dado un array, retorna otro sin valores repetidos.
 * @param array
 */
export function distinct(array) {
    return array.filter((v, i, a) => a.indexOf(v) === i);
}
/**
 * Dado un array, retorna solo los items definidos (que no son null ni undefined)
 * @param array
 */
export function getDefinedItems(array) {
    return array.filter(isDefined);
}
//# sourceMappingURL=array.js.map